type ViewLocationRating = 'Neutral' | 'Beneficial' | 'Adverse';

export type PropertyData = {
  id: string;
  lotSize: number; // square feet
  effectiveAge: number;
  economicLife: number; // max effective age for this property
  viewLocationRating: ViewLocationRating;
  siteValue: number; // in dollars
  improvementValue: number; // in dollars
  improvementValuePerAgeYear: number; // in dollars per year
  salePrice: number; // in dollars
};

type Curve = {
  a: number;
  b: number;
  offsets: {
    Neutral: number;
    Beneficial?: number;
    Adverse?: number;
  };
};

export const getViewLocationRating = (siteView?: string): ViewLocationRating => {
  const view = siteView?.toLowerCase() || '';

  // Beneficial indicators (simple heuristic for now)
  if (view.startsWith('b')) {
    return 'Beneficial';
  }

  // Adverse indicators
  if (view.startsWith('a')) {
    return 'Adverse';
  }

  return 'Neutral';
};

// Build base curve from most common view type, then compute Beneficial/Adverse offsets (5–10% bands)
export const buildCurve = (allComps: PropertyData[]): Curve => {
  const comps = allComps;

  // Determine most common view type
  const counts = comps.reduce(
    (acc, p) => {
      acc[p.viewLocationRating] = (acc[p.viewLocationRating] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  const mostCommonViewType = (Object.keys(counts) as Array<ViewLocationRating>).sort(
    (a, b) => (counts[b] ?? 0) - (counts[a] ?? 0),
  )[0];
  let baseSet = comps.filter((p) => p.viewLocationRating === mostCommonViewType);
  if (baseSet.length < 3) {
    baseSet = comps;
  }

  // Fit power curve on base set (per-SF)
  const sorted = [...baseSet].sort((a, b) => a.lotSize - b.lotSize);
  const points = sorted.map((c) => ({
    x: Math.log(c.lotSize),
    y: Math.log(c.siteValue / c.lotSize),
  }));

  const n = points.length;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumXX = points.reduce((s, p) => s + p.x * p.x, 0);

  // Check if all lot sizes are identical (which would cause division by zero)
  const denominator = n * sumXX - sumX * sumX;
  let b: number;

  if (Math.abs(denominator) < 1e-10) {
    // All lot sizes are identical, use a default slope
    b = -0.35; // Default middle value between MIN_SLOPE and MAX_SLOPE
  } else {
    b = (n * sumXY - sumX * sumY) / denominator;
    const MIN_SLOPE = -0.5;
    const MAX_SLOPE = -0.2;
    if (b > MAX_SLOPE) b = MAX_SLOPE;
    if (b < MIN_SLOPE) b = MIN_SLOPE;
  }

  const intercept = sumY / n - b * (sumX / n);
  const a = Math.exp(intercept);

  // Helper to compute per-SF from base curve
  const basePerSF = (lotSize: number) => a * lotSize ** b;
  const perSFFor = (comp: PropertyData) => comp.siteValue / comp.lotSize;

  // Find best offsets within bands to fit Beneficial and Adverse comps
  const findBestOffset = (subset: PropertyData[], minPct: number, maxPct: number, step = 0.005) => {
    if (subset.length === 0) return undefined;
    let bestPct = minPct;
    let bestError = Number.POSITIVE_INFINITY;
    for (let pct = minPct; pct <= maxPct + 1e-9; pct += step) {
      const sse = subset.reduce((sum, c) => {
        const pred = basePerSF(c.lotSize) * (1 + pct);
        const act = perSFFor(c);
        const diff = pred - act;
        return sum + diff * diff;
      }, 0);

      if (sse < bestError) {
        bestError = sse;
        bestPct = Number(pct.toFixed(3));
      }
    }
    return bestPct;
  };

  const beneficialComps = comps.filter((c) => c.viewLocationRating === 'Beneficial');
  const adverseComps = comps.filter((c) => c.viewLocationRating === 'Adverse');
  const beneficialOffset = findBestOffset(beneficialComps, 0.05, 0.1) ?? 0.07;
  const adverseOffset = findBestOffset(adverseComps, -0.1, -0.05) ?? -0.07;

  return { a, b, offsets: { Neutral: 0, Beneficial: beneficialOffset, Adverse: adverseOffset } };
};

export const getExpectedPerSF = (
  lotSize: number,
  view: ViewLocationRating,
  curve: Curve,
): number => {
  const base = curve.a * lotSize ** curve.b;

  let offsetPct = 0;
  if (view === 'Beneficial') {
    offsetPct = curve.offsets.Beneficial ?? 0;
  } else if (view === 'Adverse') {
    offsetPct = curve.offsets.Adverse ?? 0;
  }

  return base * (1 + offsetPct);
};

// Align site values to the curve via effective age adjustments
export const alignSiteValuesToCurve = (
  comps: PropertyData[],
  curve: Curve,
): Record<string, number> => {
  const adjustedAges: Record<string, number> = {};

  comps.forEach((comp) => {
    const expectedPerSF = getExpectedPerSF(comp.lotSize, comp.viewLocationRating, curve);
    const optimalSiteValue = expectedPerSF * comp.lotSize;

    // Iterate trying 1,2,3,etc age adjustments until the site value per SF approaches the optimal
    let ageAdjustment = 0;
    let lastDifference = optimalSiteValue - comp.siteValue;

    while (true) {
      if (lastDifference < 0) {
        if (comp.effectiveAge + ageAdjustment - 1 > comp.economicLife) {
          break;
        }
        const newSiteValue = comp.siteValue + comp.improvementValuePerAgeYear * (ageAdjustment - 1);
        const newDifference = optimalSiteValue - newSiteValue;
        if (Math.abs(newDifference) < Math.abs(lastDifference)) {
          ageAdjustment--;
          lastDifference = newDifference;
        } else {
          break;
        }
      } else {
        if (comp.effectiveAge + ageAdjustment + 1 < 0) {
          break;
        }
        const newSiteValue = comp.siteValue + comp.improvementValuePerAgeYear * (ageAdjustment + 1);
        const newDifference = optimalSiteValue - newSiteValue;
        if (Math.abs(newDifference) < Math.abs(lastDifference)) {
          ageAdjustment++;
          lastDifference = newDifference;
        } else {
          break;
        }
      }
    }

    // Ensure the adjusted age is between 0 and the property's economic life
    const adjustedAge = Math.min(comp.economicLife, Math.max(0, comp.effectiveAge + ageAdjustment));
    adjustedAges[comp.id] = adjustedAge;
  });

  return adjustedAges;
};
